import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { useT } from "../lib/use-t";

type Mode = "verifyEmail" | "resetPassword" | "verifyAndChangeEmail" | "recoverEmail";

type State =
  | { kind: "loading" }
  | { kind: "needs_new_password"; oobCode: string; email: string }
  | { kind: "success"; mode: Mode }
  | { kind: "error"; message: string };

export function AuthActionPage() {
  const t = useT();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const mode = params.get("mode") as Mode | null;
  const oobCode = params.get("oobCode");
  const continueUrl = params.get("continueUrl");

  const [state, setState] = useState<State>({ kind: "loading" });
  const [newPw, setNewPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function humanError(err: unknown): string {
    const code = (err as { code?: string }).code;
    if (code === "auth/expired-action-code") return t("authAction.errors.expiredCode");
    if (code === "auth/invalid-action-code") return t("authAction.errors.invalidCode");
    if (code === "auth/user-disabled") return t("authAction.errors.userDisabled");
    if (code === "auth/user-not-found") return t("authAction.errors.userNotFound");
    if (code === "auth/weak-password") return t("authAction.errors.weakPassword");
    if (err instanceof Error) return err.message;
    return t("chrome.common.unknownError");
  }

  function titleFor(): string {
    if (state.kind === "error") return t("authAction.errorTitle");
    if (state.kind === "needs_new_password") return t("authAction.passwordResetTitle");
    if (state.kind === "success") {
      if (mode === "resetPassword") return t("authAction.passwordResetDoneTitle");
      if (mode === "verifyEmail") return t("authAction.verifyEmailDoneTitle");
      if (mode === "verifyAndChangeEmail") return t("authAction.verifyAndChangeEmailDoneTitle");
      if (mode === "recoverEmail") return t("authAction.recoverEmailDoneTitle");
    }
    if (mode === "resetPassword") return t("authAction.passwordResetTitle");
    if (mode === "verifyEmail") return t("authAction.verifyEmailTitle");
    if (mode === "verifyAndChangeEmail") return t("authAction.verifyAndChangeEmailTitle");
    if (mode === "recoverEmail") return t("authAction.recoverEmailTitle");
    return t("authAction.unknownActionTitle");
  }

  function successCopy(): string {
    if (mode === "verifyEmail") return t("authAction.successVerifyEmail");
    if (mode === "resetPassword") return t("authAction.successResetPassword");
    if (mode === "verifyAndChangeEmail") return t("authAction.successVerifyAndChangeEmail");
    if (mode === "recoverEmail") return t("authAction.successRecoverEmail");
    return t("authAction.successGeneric");
  }

  useEffect(() => {
    if (!mode || !oobCode) {
      setState({ kind: "error", message: t("authAction.errors.invalidLink") });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (mode === "resetPassword") {
          // Why: verify the code first to confirm it's still valid + fetch the
          // associated email; the user submits the new password in a follow-up
          // form, then we call confirmPasswordReset.
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) setState({ kind: "needs_new_password", oobCode, email });
        } else if (
          mode === "verifyEmail" ||
          mode === "verifyAndChangeEmail" ||
          mode === "recoverEmail"
        ) {
          await applyActionCode(auth, oobCode);
          if (!cancelled) setState({ kind: "success", mode });
        } else {
          if (!cancelled) setState({ kind: "error", message: t("authAction.errors.unknownModeFmt", { mode }) });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ kind: "error", message: humanError(err) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  function next() {
    nav(continueUrl ?? "/login", { replace: true });
  }

  return (
    <main className="flex-1 mx-auto w-full max-w-xl px-5 sm:px-6 py-10 sm:py-16 space-y-8">
      <div className="space-y-2">
        <p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
          {t("authAction.eyebrow")}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--ink)]">
          {titleFor()}
        </h1>
      </div>

      <section className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 space-y-4">
        {state.kind === "loading" ? (
          <p className="text-sm text-[var(--ink-muted)]">{t("authAction.loading")}</p>
        ) : null}

        {state.kind === "needs_new_password" ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (newPw.length < 6) {
                setState({ kind: "error", message: t("authAction.errors.weakPassword") });
                return;
              }
              setSubmitting(true);
              try {
                await confirmPasswordReset(auth, state.oobCode, newPw);
                setState({ kind: "success", mode: "resetPassword" });
              } catch (err) {
                setState({ kind: "error", message: humanError(err) });
              } finally {
                setSubmitting(false);
              }
            }}
            className="space-y-3"
          >
            <p className="text-sm leading-relaxed text-[var(--ink-muted)]">
              {t("authAction.newPasswordDescription", { email: state.email })}
            </p>
            <Input
              label={t("authAction.newPasswordLabel")}
              name="newPassword"
              type="password"
              required
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
            <Button type="submit" variant="primary" size="md" disabled={submitting}>
              {t("authAction.setPasswordSubmit")}
            </Button>
          </form>
        ) : null}

        {state.kind === "success" ? (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-[var(--ink)]">
              <span className="text-[var(--pass)]">✓</span> {successCopy()}
            </p>
            <Button type="button" variant="primary" size="md" onClick={next}>
              {continueUrl ? t("authAction.continueButton") : t("authAction.backToLogin")}
            </Button>
          </div>
        ) : null}

        {state.kind === "error" ? (
          <div className="space-y-3">
            <p role="alert" className="text-sm leading-relaxed text-[var(--fail)]">
              {state.message}
            </p>
            <Button type="button" variant="ghost" size="md" onClick={() => nav("/login")}>
              {t("authAction.backToLogin")}
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
